import React, { FC, SyntheticEvent, useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { SurveyTemporalFilterType } from '../../Surveys/surveyInterface';
import { CloseButtonStyled } from '../ActionsCard/styled';
import { DashboardFromApi } from '../dashboardInterfaces';
import { FilterPanelStyled, FilterPanelFooterStyled, ResetPanelButtonStyled } from '../styled';
import ComparePeriodToggle from './comparePeriodToggle';
import ComparisonDateRangeSelect from './DateRangeSelect/comparisonDateRangeSelect';
import DateRangeSelect from './DateRangeSelect/dateRangeSelect';
import SelectedFilterSummary from './selectedFilterSummary';
import { AccordionStyled, AccordionDetailsStyled, AccordionSummaryStyled, TemporalScopeStyled, TemporalScopeElementStyled, TemporaFilterImageStyled, DateRangeComparisonStyled } from './styled';
import TemporalScopeSelect from './TemporalScopeSelect/temporalScopeSelect';

interface TemporalScopeProps {
    temporalScopes: DashboardFromApi['temporalScopesConfig'];
    handleSelectedScope: ([scopeId, modalityId]: [string, Array<string> | null]) => void;
    handleSelectedScopeLabel: ([scopeId, scopeLabel, modalitiesLabel]: [number, string, string | null]) => void;
    maxDate?: Date;
    minDate?: Date;
    selectedScopeLabel?: any;
    selectedScope?: any;
    dateRange?: any;
    handleChangeDateRangeScope: (dateRange : any) => void;
    temporalFilterType: string;
    isForHotAlert: boolean;
    comparedScope?: any;
    comparedDateRange?: any;
    handleChangeComparedDateRangeScope?: (dateRange: any) => void;
    handleSelectedComparedScope?: ([scopeId, modalityId]: [string, Array<string> | null]) => void;
    isComparedToPeriod?: boolean;
    isComparedToGlobal?: boolean;
    handleToggleCompareToPeriod?: (isComparedToPeriod: boolean) => void;
    handleResetTemporalScope?: () => void;
}

const TemporalScope: FC<TemporalScopeProps> = ({ temporalScopes, handleSelectedScope, handleSelectedScopeLabel, dateRange, handleChangeDateRangeScope, selectedScopeLabel, selectedScope, temporalFilterType, minDate, maxDate, isForHotAlert, comparedScope = [], comparedDateRange, handleChangeComparedDateRangeScope, handleSelectedComparedScope, isComparedToPeriod = false, isComparedToGlobal = false, handleToggleCompareToPeriod, handleResetTemporalScope }) => {
    const { t } = useTranslation('dashboard');
    const [expanded, setExpanded] = useState<string | false>(false);
    const hasDateRange = dateRange?.selection?.startDate && dateRange?.selection?.endDate;
    const selectedDateRangeLabel = (() => {
        if (!hasDateRange) {
            return '';
        }
        const from = dateRange.selection.startDate.toLocaleDateString('fr-FR');
        const to = dateRange.selection.endDate.toLocaleDateString('fr-FR');

        return from === to ? `Le ${from}` : `${from} - ${to}`;
    })();
    const comparedDateRangeLabel = (isComparedToPeriod && comparedDateRange?.selection?.startDate && comparedDateRange?.selection?.endDate)
        ? ` - ${t('temporalScope.comparison')} ${comparedDateRange.selection.startDate.toLocaleDateString('fr-FR')} - ${comparedDateRange.selection.endDate.toLocaleDateString('fr-FR')}`
        : '';
    const dateRangeLabel = `${selectedDateRangeLabel}${comparedDateRangeLabel}`;
    const accordionRef = useRef<HTMLDivElement>(null);
    // A wave picked on one side is taken out of the other side's choices, since
    // comparing a period with itself says nothing.
    const modalitiesTakenOn = (scopes: any, scopeId: string): Array<string> => scopes
        .filter((scope: any) => scope.scopeId === scopeId)
        .flatMap((scope: any) => scope.modalityId);
    // The compared waves are only known by id, the readable labels live in the
    // scope configuration.
    const comparedModalitiesLabel = isComparedToPeriod
        ? temporalScopes
            .flatMap((temporal) => {
                const comparedModalityIds = modalitiesTakenOn(comparedScope, Object.keys(temporal.scope)[0]);

                return Object.entries(temporal.modalities)
                    .filter(([modalityId]) => comparedModalityIds.includes(modalityId))
                    .map(([, modalityLabel]) => modalityLabel);
            })
            .join(', ')
        : '';
    const handleChange = (panel: string) => (event: SyntheticEvent, isExpanded: boolean) => {
        setExpanded(isExpanded ? panel : false);
    };

    useEffect(() => {
        if (temporalFilterType !== SurveyTemporalFilterType.VAGUE) {
            return;
        }
        temporalScopes.forEach((temp) => {
            if (!temp.hasInitWithLastValue) {
                return;
            }
            const maxScopeModality = Math.max(...Object.keys(temp.modalities).map(Number));
            const scopeId = Object.keys(temp.scope)[0];
            const scopeLabel = Object.values(temp.scope)[0];
            const maxScopeModalityLabel = Object.entries(temp.modalities)
                .filter((modality) => String(maxScopeModality) === modality[0])
                .map((modality) => modality[1])
                .join(', ');

            handleSelectedScope([scopeId, [`${maxScopeModality}`]]);
            handleSelectedScopeLabel([parseInt(scopeId, 10), scopeLabel, maxScopeModalityLabel]);
        });
    }, [temporalScopes, temporalFilterType]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as HTMLElement;

            // Check if click is inside a MUI Select menu (portaled element)
            const isInsideMenu = target.closest('.MuiMenu-root, .MuiPopover-root, [role="presentation"]');

            if (accordionRef.current && !accordionRef.current.contains(target) && !isInsideMenu && expanded === 'panel1') {
                setExpanded(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [expanded]);

    return (
        <AccordionStyled ref={accordionRef} expanded={expanded === 'panel1'} height={expanded ? '54px' : '54px'} onChange={handleChange('panel1')}>
            <AccordionSummaryStyled
                $hasComparison={Boolean(comparedDateRangeLabel || comparedModalitiesLabel)}
                $hasSelection={Boolean(temporalFilterType === SurveyTemporalFilterType.DATE ? hasDateRange : selectedScopeLabel.length > 0)}
                aria-controls="panel1a-content"
                id="panel1a-header"
            >
                <TemporaFilterImageStyled />
                { temporalFilterType === SurveyTemporalFilterType.VAGUE && selectedScopeLabel.length > 0
            && selectedScopeLabel.map((temporalLabel: any) => (
                <SelectedFilterSummary
                    key={temporalLabel.scopeLabel}
                    selectedValuesLabel={temporalLabel.modalitiesLabel}
                />
            ))}
                { temporalFilterType === SurveyTemporalFilterType.DATE && dateRangeLabel && (
                    <SelectedFilterSummary
                        selectedValuesLabel={dateRangeLabel}
                    />
                )}
                { comparedModalitiesLabel && (
                    <SelectedFilterSummary
                        selectedValuesLabel={`- ${t('temporalScope.comparedWith')} ${comparedModalitiesLabel}`}
                    />
                )}
            </AccordionSummaryStyled>
            <AccordionDetailsStyled $fullWidth={!isForHotAlert} $positionXY={isForHotAlert ? 'right: 0;' : ''}>
                <TemporalScopeStyled>
                    <FilterPanelStyled>
                        {temporalFilterType === SurveyTemporalFilterType.DATE && (
                            <DateRangeComparisonStyled>
                                <DateRangeSelect
                                    dateRange={dateRange}
                                    handleChangeDateRangeScope={handleChangeDateRangeScope}
                                    maxDate={maxDate}
                                    minDate={minDate}
                                />
                                {handleToggleCompareToPeriod && (
                                    <ComparePeriodToggle
                                        handleToggleCompareToPeriod={handleToggleCompareToPeriod}
                                        isComparedToGlobal={isComparedToGlobal}
                                        isComparedToPeriod={isComparedToPeriod}
                                    />
                                )}
                                {isComparedToPeriod && handleChangeComparedDateRangeScope && (
                                    <ComparisonDateRangeSelect
                                        comparedDateRange={comparedDateRange}
                                        handleChangeComparedDateRangeScope={handleChangeComparedDateRangeScope}
                                        mainDateRange={dateRange}
                                        maxDate={maxDate}
                                        minDate={minDate}
                                    />
                                )}
                            </DateRangeComparisonStyled>
                        )}
                        { temporalFilterType === SurveyTemporalFilterType.VAGUE && temporalScopes.length > 0
                    && (
                        <TemporalScopeElementStyled>
                            {temporalScopes.map((temporal) => (
                                <TemporalScopeSelect
                                    key={Object.keys(temporal.scope)[0]}
                                    handleSelectedScope={handleSelectedScope}
                                    handleSelectedScopeLabel={handleSelectedScopeLabel}
                                    selectedScope={selectedScope.filter((scope: any) => scope.scopeId === Object.keys(temporal.scope)[0])}
                                    temporal={temporal}
                                    unavailableModalityIds={modalitiesTakenOn(comparedScope, Object.keys(temporal.scope)[0])}
                                />
                            ))}
                        </TemporalScopeElementStyled>
                    )}
                        { temporalFilterType === SurveyTemporalFilterType.VAGUE && temporalScopes.length > 0 && handleToggleCompareToPeriod && (
                            <TemporalScopeElementStyled>
                                <ComparePeriodToggle
                                    handleToggleCompareToPeriod={handleToggleCompareToPeriod}
                                    isComparedToGlobal={isComparedToGlobal}
                                    isComparedToPeriod={isComparedToPeriod}
                                />
                                {handleSelectedComparedScope && temporalScopes.map((temporal) => (
                                    <TemporalScopeSelect
                                        key={`compared-${Object.keys(temporal.scope)[0]}`}
                                        disabled={!isComparedToPeriod}
                                        handleSelectedScope={handleSelectedComparedScope}
                                        // Compared labels are not summarised in the closed bar.
                                        handleSelectedScopeLabel={() => {}}
                                        selectedScope={comparedScope.filter((scope: any) => scope.scopeId === Object.keys(temporal.scope)[0])}
                                        temporal={temporal}
                                        unavailableModalityIds={modalitiesTakenOn(selectedScope, Object.keys(temporal.scope)[0])}
                                    />
                                ))}
                            </TemporalScopeElementStyled>
                        )}
                        {handleResetTemporalScope && (
                            <FilterPanelFooterStyled>
                                <ResetPanelButtonStyled onClick={handleResetTemporalScope} type="button">
                                    <CloseButtonStyled sx={{ width: '18px', height: '18px' }} />
                                    {t('temporalScope.reset')}
                                </ResetPanelButtonStyled>
                            </FilterPanelFooterStyled>
                        )}
                    </FilterPanelStyled>
                </TemporalScopeStyled>
            </AccordionDetailsStyled>
        </AccordionStyled>
    );
};

export default TemporalScope;
